import { CommonDataKeywordItem } from "@api/useCommonData";
import { useCommonDataContext, useGetHeatScore, useGetMacStage } from "@core/CommonDataContext";
import { useCallback } from "react";
import { MacStage } from "@models/MacStage";
import { HeatScore } from "@models/HeatScore";

type PartialCommonDataKeywordItem = Omit<CommonDataKeywordItem, 'id' | 'description' | 'macStage' | 'heatScore' | 'name' | 'subCategoriesIds'>

export type KeywordBase = Partial<PartialCommonDataKeywordItem> & {
    id: NonNullable<CommonDataKeywordItem["id"]>;
    name: string;
    description?: string;
    macStage: MacStage | null;
    macStageTitle: string;
    heatScore: HeatScore | null;
    heatScoreTitle: string,
    subCategoriesIds: NonNullable<CommonDataKeywordItem["subCategoriesIds"]>;
}

export type KeywordTableColumn<I> = {
    id: I;
    name: string;
    value: number | null;
    tableValue: string;
    copyTableValue: string;
    sortValue: number | null;
    norm?: number | null;
};

export function useGetKeywordBaseData<T extends Array<keyof PartialCommonDataKeywordItem>>(additionalFields?: T) {
    const { keywords = [] } = useCommonDataContext();
    const getMacStage = useGetMacStage();
    const getHeatScore = useGetHeatScore();

    return useCallback(
        (id: NonNullable<CommonDataKeywordItem["id"]>): KeywordBase & Pick<PartialCommonDataKeywordItem, T[number]> => {
            const keyword = keywords[id] ?? null;
            if (!keyword) {
                const baseResult = {
                    id,
                    name: '',
                    description: '',
                    macStage: null,
                    macStageTitle: '-',
                    heatScore: null,
                    heatScoreTitle: '-',
                    subCategoriesIds: [],
                };

                if (additionalFields?.length) {
                    for (const field of additionalFields) {
                        (baseResult as any)[field] = undefined;
                    }
                }

                return baseResult as any;
            }

            const keywordBase: KeywordBase = {
                id,
                name: (keyword?.name ?? '').toUpperCase(),
                description: keyword?.description ?? '',
                macStage: keyword?.macStage === 0 ? null : keyword?.macStage ?? null,
                macStageTitle: getMacStage(keyword?.macStage)?.name ?? '-',
                heatScore: keyword?.heatScore ?? null,
                heatScoreTitle: getHeatScore(keyword?.heatScore)?.name ?? '-',
                subCategoriesIds: keyword?.subCategoriesIds ?? [],
            };

            if (additionalFields?.length) {
                for (const field of additionalFields) {
                    (keywordBase as any)[field] = keyword[field] ?? undefined;
                }
            }

            return keywordBase as any;
        },
        [keywords, getMacStage, getHeatScore, additionalFields]
    );
}

